import { useEffect, useState, useCallback, useMemo } from 'react';
import { adminApi } from '../../services/portalApi';
import LiveMap from '../../components/LiveMap';
import { useDefaultCentre } from '../../hooks/useDefaultCentre';
import type { Drone } from '../../types';

const STATE_LABELS: Record<string, string> = {
  docked_charging: 'At base (charging)',
  idle_ready: 'At base (ready)',
  patrolling: 'Patrolling',
  responding: 'Responding',
  returning_home: 'Returning to base',
  low_battery_grounded: 'Grounded (low battery)',
  crashed: 'Crashed — recovering',
};

const STATE_COLOURS: Record<string, string> = {
  docked_charging: 'bg-amber-100 text-amber-800',
  idle_ready: 'bg-green-100 text-green-800',
  patrolling: 'bg-blue-100 text-blue-800',
  responding: 'bg-red-100 text-red-800',
  returning_home: 'bg-blue-100 text-blue-800',
  low_battery_grounded: 'bg-red-100 text-red-800',
  crashed: 'bg-red-200 text-red-900',
};

function fmtTime(epochMs: number): string {
  return new Date(epochMs).toTimeString().slice(0, 8);
}

function fmtDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

function fmtWind(kmh: number, deg: number): string {
  if (kmh <= 0) return 'Calm';
  const point = COMPASS[Math.round(((deg % 360) / 45)) % 8];
  return `${kmh.toFixed(0)} km/h ${point}`;
}

// Thresholds mirror the backend: ?MAX_SAFE_WIND_KMH gates new launches,
// ?CRASH_WIND_KMH downs an airborne drone.
const WIND_LAUNCH_LIMIT_KMH = 25;
const WIND_CRASH_KMH = 40;

function windClass(kmh: number): string {
  if (kmh > WIND_CRASH_KMH) return 'text-red-700 font-semibold';
  if (kmh > WIND_LAUNCH_LIMIT_KMH) return 'text-amber-700 font-semibold';
  return 'text-gray-900';
}

function Stat({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <span className="whitespace-nowrap">
      <span className="text-gray-500">{label}: </span>
      <span className={`font-medium tabular-nums ${valueClass ?? 'text-gray-900'}`}>{value}</span>
    </span>
  );
}

function BatteryBar({ pct }: { pct: number }) {
  const color = pct < 15 ? 'bg-red-500' : pct < 40 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <span className="text-sm font-medium text-gray-900 tabular-nums">{pct.toFixed(0)}%</span>
    </div>
  );
}

function DroneCard({ drone }: { drone: Drone }) {
  const isAlertMonitoring = drone.mission_state === 'responding' && drone.flight_phase === 'hovering';
  // Charged and willing, but the controller refuses to launch into this
  // wind — without saying so the card reads identically to a drone that is
  // simply idle, which looks like a fault rather than a weather hold.
  const windGrounded =
    drone.mission_state === 'idle_ready' && drone.wind_kmh > WIND_LAUNCH_LIMIT_KMH;
  const label = isAlertMonitoring
    ? 'Alert monitoring'
    : windGrounded
      ? 'At base (ready — grounded, wind)'
      : STATE_LABELS[drone.mission_state] ?? drone.mission_state;
  const colour = windGrounded
    ? 'bg-amber-100 text-amber-800'
    : STATE_COLOURS[drone.mission_state] ?? 'bg-gray-100 text-gray-800';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm text-gray-900">{drone.drone_id}</span>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colour}`}>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Battery</span>
        <BatteryBar pct={drone.battery_pct} />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm overflow-x-auto">
        <Stat
          label="Location"
          value={
            drone.lat !== null && drone.lng !== null
              ? `${drone.lat.toFixed(5)}, ${drone.lng.toFixed(5)}`
              : '—'
          }
        />
        <Stat label="Speed" value={`${drone.speed_kmh.toFixed(0)} km/h`} />
        <Stat
          label="Wind"
          value={fmtWind(drone.wind_kmh, drone.wind_direction_deg)}
          valueClass={windClass(drone.wind_kmh)}
        />
        <Stat
          label="Altitude (AGL / ASL)"
          value={`${drone.altitude_agl_m.toFixed(0)}m / ${drone.altitude_asl_m.toFixed(0)}m`}
        />
        <Stat label="Last seen" value={fmtTime(drone.last_seen)} />
        <Stat label="Patrols" value={`${drone.patrol_count}`} />
        <Stat
          label="Last patrol start"
          value={drone.last_patrol_started_at !== null ? fmtTime(drone.last_patrol_started_at) : '—'}
        />
        <Stat
          label="Last patrol duration"
          value={drone.last_patrol_duration_s !== null ? fmtDuration(drone.last_patrol_duration_s) : '—'}
        />
        <Stat label="Alarms attended" value={`${drone.alarms_attended}`} />
        <Stat label="Crashes" value={`${drone.crashes}`} />
      </div>
    </div>
  );
}

export default function Drones() {
  const [drones, setDrones] = useState<Drone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const defaultCentre = useDefaultCentre();

  const load = useCallback(async () => {
    try {
      const data = await adminApi.listDrones();
      setDrones(data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = setInterval(load, 5_000);
    return () => clearInterval(id);
  }, [load]);

  // Centre on the drone's fixed home base, not its live position — using
  // primitive lat/lng as the memo dependency (not the drones array, which is
  // a new reference every 5s poll) keeps this reference-stable across polls,
  // so the map never re-fires its fly-to-centre animation and stays put.
  const homeLat = drones[0]?.home_lat ?? null;
  const homeLng = drones[0]?.home_lng ?? null;
  const centre = useMemo<[number, number]>(() => {
    return homeLat !== null && homeLng !== null ? [homeLat, homeLng] : defaultCentre;
  }, [homeLat, homeLng, defaultCentre]);

  return (
    <div className="px-6 py-6 max-w-6xl space-y-6">
      <h1 className="text-lg font-semibold text-gray-900">Drones</h1>

      {!loading && !error && (
        <div className="relative">
          <div className="h-[70vh] rounded-xl overflow-hidden border border-gray-200">
            <LiveMap guards={[]} drones={drones} centre={centre} zoom={16} className="h-full w-full" />
          </div>
          <div className="absolute top-3 right-3 z-[1000] bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow text-xs text-gray-500">
            Auto-refreshes every 5s
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {!loading && error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">Could not load drone status.</p>
        </div>
      )}

      {!loading && !error && drones.length === 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-sm text-gray-500">No drones configured for this estate.</p>
        </div>
      )}

      {!loading && drones.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {drones.map((d) => (
            <DroneCard key={d.drone_id} drone={d} />
          ))}
        </div>
      )}
    </div>
  );
}
